import { DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { LimeGearCharts } from 'app/modules/shared/classes/chart-data';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { HierarchyFiltersCustomOptions } from 'app/modules/shared/components/main-filters/hierarchy-filter-options/hierarchy-flters-custom-options';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import {
    IHierachyFilterCacheUrlInfo,
    UserLocation,
} from 'app/modules/shared/interfaces/user-location';
import { FiltersService } from 'app/modules/shared/services/filters.service';
import * as Highcharts from 'highcharts';
import { IProfitabilityDataModel } from '../../interfaces/profitability-data';
import { IRevenueDataModel, IRevenueParams } from '../../interfaces/revenue-data';
import { RevenueService } from '../../services/revenue.service';

@Component({
    selector: 'app-incentives-insights-revenue-trends',
    templateUrl: './incentives-insights-revenue-trends.component.html',
    styleUrls: ['./incentives-insights-revenue-trends.component.scss'],
})
export class IncentivesInsightsRevenueTrendsComponent implements OnInit {
    action: NoDataActionType;
    data: IRevenueDataModel[];
    displayOptions: DisplayOptions[];
    loading: boolean = false;
    noDataType: NoDataTypes;
    pageLoading: boolean = true;
    revenueParams: IRevenueParams;
    showNames: boolean;
    limeGearCharts: LimeGearCharts.LimeGearChart;
    gridView: GridDataResult;
    viewByParam: string = 'trend';
    hierarchyCacheInfo: HierarchyFiltersCustomOptions = new HierarchyFiltersCustomOptions();
    hierarchyCachePageInfo: IHierachyFilterCacheUrlInfo;
    roleData: UserLocation[];
    entityList: Entity[] = [];
    selectedEntity: Entity[] = [
        {
            entityIdentifier: '4',
            entity: 'Branch',
        },
    ];
    entityListSettings: any;
    pageHeadline: IPageHeadline = {
        module: 'financials',
        moduleIcon: 'fa-dollar-sign',
        pageTitle: 'Revenue Trends',
        linkArray: [
            {
                moduleLink: 'financials',
                linkTitle: 'Financials Home',
                showLink: true,
                isHome: true,
            },
            {
                moduleLink: 'financials/insights',
                linkTitle: 'Financials Insights',
                showLink: true,
            },
        ],
    };
    noDataForCompany: boolean;
    dataLoading: boolean;
    skip = 0;
    hFilterJSON: string = null;
    fromDatePeriod: string;
    entityIdentifier: string;
    roleName: string = 'Branch';
    pageSize = 10;
    type: 'numeric' | 'input' = 'input';
    pageable = {
        refresh: true,
        info: true,
        type: this.type,
        previousNext: true,
        pageSizes: [5, 10, 20],
    };
    sort: SortDescriptor[] = [{ field: 'totalRevenue', dir: 'desc' }];
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };
    searchTerm: string = '';

    constructor(
        private reusableFunctions: ReusableFunctions,
        private router: Router,
        private revenueService: RevenueService,
        private decimalPipe: DecimalPipe,
        private filterService: FiltersService
    ) {}

    ngOnInit(): void {
        this.action = NoDataActionType.pageStart;
        this.noDataType = {
            action: this.action,
            searchTerm: '',
            componentTitle: this.pageHeadline.pageTitle,
            inputSource: NoDataSourceType.largeDataCard,
        };

        this.limeGearCharts = new LimeGearCharts.LimeGearChart(
            this.router,
            this.reusableFunctions,
            this.decimalPipe
        );

        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this.router.url);
        this.hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.entityListSettings = {
            singleSelection: true,
            text: 'Select Entity',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'entity',
            primaryKey: 'entityIdentifier',
            enableFilterSelectAll: false,
        };

        this.setDisplayOptionsSelected();
        this.getFilterRoles();
        this.getData();
    }

    ngAfterViewInit() {
        this.pageLoading = false;
    }

    getFilterRoles() {
        let url = this.router.url.split('?')[0];
        this.hierarchyCacheInfo = new HierarchyFiltersCustomOptions();
        this.hierarchyCachePageInfo = this.hierarchyCacheInfo.getHierarchyCachePageInfo(url);
        this.filterService.getUsersAllLocations(this.hierarchyCachePageInfo.employeeType).subscribe(
            (data) => {
                let empIdx = data.findIndex((role) => role.roleName == 'Employee');
                if (empIdx != -1) {
                    data.splice(empIdx, 1);
                }

                this.roleData = data;
                this.roleData.forEach((role) => {
                    this.entityList.push({
                        entity: role.roleName,
                        entityIdentifier: role.roleLevel.toString(),
                    });
                });

                if (this.roleName != null) {
                    this.getEntityData();
                }
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.skip = 0;
        this.displayOptions = event;
        const onlyViewByChanged = this.setDisplayOptionsSelected();
        if (!onlyViewByChanged) {
            this.getData();
        } else {
            this.updateGrid();
        }
    }

    setDisplayOptionsSelected() {
        const fromDate = this.displayOptions.find(
            (item) => item.optionKey === 'dateRangeFinancials'
        );
        const isDateSame = this.fromDatePeriod === fromDate.selectedOptions;
        this.fromDatePeriod = fromDate?.selectedOptions ?? '';

        const viewByAmt =
            this.displayOptions.find((item) => item.optionKey === 'viewByAmt')?.selectedOptions ??
            '';

        this.viewByParam = viewByAmt === 'Amount' ? 'trend' : 'change';

        return isDateSame;
    }

    getData() {
        this.loading = true;
        this.revenueParams = {
            fromDatePeriod: this.fromDatePeriod,
            clientRoleName: this.selectedEntity[0].entity,
            hFilterJSON: this.hFilterJSON,
        };

        this.revenueService.getRevenueEntityData(this.revenueParams).subscribe(
            (data) => {
                if (!data) data = [];
                if (data.length < 1) {
                    this.setNoData();
                }

                this.prepareData(data);
                this.loading = false;
            },
            (error) => {
                this.setNoData();
                this.loading = false;
                throw new Error(error);
            }
        );
    }

    prepareData(entityData) {
        const yearMonthCalendar: string[] = Array.from(
            new Set(entityData.map((e) => e.yearMonthCalendar))
        );
        const sanitizedEntityData = {};
        for (let data of entityData) {
            const isExpense = data.accountType === 'Expense';
            const hierarchyName = data.hierarchyName;
            if (sanitizedEntityData[hierarchyName]) {
                if (isExpense) {
                    sanitizedEntityData[hierarchyName].expenseValues.push(data.totalPostAmt);
                    sanitizedEntityData[hierarchyName].expenseChangeValues.push(
                        data.changePerPeriod
                    );
                    sanitizedEntityData[hierarchyName].netPeriodValues.push(data.periodNetAmount);
                    sanitizedEntityData[hierarchyName].netIncome += data.periodNetAmount;
                } else {
                    sanitizedEntityData[hierarchyName].revenueValues.push(data.totalPostAmt);
                    sanitizedEntityData[hierarchyName].revenueChangeValues.push(
                        data.changePerPeriod
                    );
                }
            } else {
                sanitizedEntityData[hierarchyName] = {
                    hierarchyName: hierarchyName,
                    expenseValues: isExpense ? [data.totalPostAmt] : [],
                    expenseChangeValues: isExpense ? [data.changePerPeriod] : [],
                    revenueValues: isExpense ? [] : [data.totalPostAmt],
                    revenueChangeValues: isExpense ? [] : [data.changePerPeriod],
                    totalExpense: data.totalEntityExpense,
                    totalRevenue: data.totalEntityRevenue,
                    netPeriodValues: isExpense ? [data.periodNetAmount] : [],
                    netIncome: isExpense ? data.periodNetAmount : 0,
                };
            }
            sanitizedEntityData[hierarchyName].titleValues = yearMonthCalendar;
        }

        this.data = Object.values(sanitizedEntityData);
        for (let entity of this.data) {
            entity['netPeriodChangeValues'] = entity.revenueChangeValues.map((item, index) => {
                return item - entity.expenseChangeValues[index];
            });
        }

        this.updateGrid();
    }

    updateGrid() {
        const searchData = this.searchResults();
        const orderedData = orderBy(searchData, this.sort);
        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: this.data.length,
        };

        setTimeout(() => {
            this.drawCharts(this.gridView.data);
        }, 0);
    }

    searchRows(event) {
        this.skip = 0;
        this.searchTerm = event;
        this.updateGrid();
    }

    searchResults() {
        let searchedData = [];
        if (this.data?.length > 0 && this.searchTerm && this.searchTerm.length > 0) {
            let searchTermTokens = this.searchTerm.split(' ');
            searchedData = filterBy(this.data, {
                logic: 'or',
                filters: [
                    {
                        field: 'hierarchyName',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                ],
            });
            if (searchTermTokens.length > 1) {
                searchedData = filterBy(searchedData, {
                    logic: 'or',
                    filters: [
                        {
                            field: 'hierarchyName',
                            operator: 'contains',
                            value: searchTermTokens[1],
                            ignoreCase: true,
                        },
                    ],
                });
            }
        } else {
            searchedData = this.data;
        }
        if (searchedData?.length === 0) {
            this.noDataForCompany = true;
        }
        return searchedData;
    }

    drawCharts(data: IRevenueDataModel[]): void {
        const seriesType = this.viewByParam === 'trend' ? 'area' : 'column';
        const change = this.viewByParam === 'trend' ? '' : 'Change';
        const dataType = 'currency';

        for (let i = 0; i < data.length; i++) {
            new Highcharts.Chart(
                `revenueValues${data[i].hierarchyName}`,
                this.limeGearCharts.createRevenueSparkline(
                    data[i][`revenue${change}Values`],
                    data[i].titleValues,
                    seriesType,
                    dataType
                )
            );
            new Highcharts.Chart(
                `expenseValues${data[i].hierarchyName}`,
                this.limeGearCharts.createRevenueSparkline(
                    data[i][`expense${change}Values`],
                    data[i].titleValues,
                    seriesType,
                    dataType
                )
            );
            new Highcharts.Chart(
                `netPeriodValues${data[i].hierarchyName}`,
                this.limeGearCharts.createRevenueSparkline(
                    data[i][`netPeriod${change}Values`],
                    data[i].titleValues,
                    seriesType,
                    dataType
                )
            );
        }
    }

    setNoData() {
        this.noDataForCompany = true;
        this.loading = false;
        this.dataLoading = false;
        this.noDataType.action = this.action;
    }

    onSelectionChange(event) {
        this.skip = 0;
        this.entityIdentifier = this.selectedEntity[0].entityIdentifier;
        this.roleName = this.selectedEntity[0].entity;
        this.gridView = {
            data: [],
            total: 0,
        };
        this.getEntityData();
    }

    getEntityData() {
        this.entityList = [...this.entityList];
        let recordInList = this.entityList.find((x) => x.entity == this.roleName);
        if (recordInList) {
            this.selectedEntity = [recordInList];
            this.pageHeadline.pageTitle = `${this.roleName} Revenue Trends`;
        } else {
            this.selectedEntity = [];
            this.pageHeadline.pageTitle = `Branch Revenue Trends`;
        }

        this.getData();
    }

    applyNewFilters(event: string) {
        this.skip = 0;
        this.hFilterJSON = event;
        this.getEntityData();
    }

    pageChange(event: PageChangeEvent): void {
        this.pageSize = event.take;
        this.skip = event.skip;
        this.updateGrid();
    }

    sortChange(sort: SortDescriptor[]): void {
        this.sort = sort;
        this.updateGrid();
    }
}

interface Entity {
    entityIdentifier: string;
    entity: string;
}
